import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Token } from '../types/supabase';
import { ExternalLink, AlertTriangle, Users, ArrowUpRight, ArrowDownRight, MessageCircle, Globe, Search, Pill, Wallet, Copy, Check, Settings, X, DollarSign, Info, Loader, Youtube } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { jupiterService } from '../services/jupiterService';
import { toast } from 'react-hot-toast';
import { createPortal } from 'react-dom';
import { getCachedQuickBuyAmount } from '../services/quickBuyService';
import { userService } from '../services/userService';
import { metadataService, TokenMetadata } from '../services/metadataService';
import { executeQuickBuy } from '../services/quickBuyService';

// Local base64 placeholder image - updated to SVG which is more reliable
const PLACEHOLDER_IMAGE = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iMzIiIGZpbGw9IiMxNjE2MUEiLz4KPHBhdGggZD0iTTQxLjggMjMuNzVIMjEuODVWNDAuMjVINDEuOFYyMy43NVoiIHN0cm9rZT0iIzM4Q0Q5QSIgc3Ryb2tlLXdpZHRoPSIyIi8+Cjwvc3ZnPg==";

// Flag to track if any Buy button is in a clicked state
const isAnyBuyButtonClicked = {current: false};

// Minimum time between clicks in milliseconds
const DEBOUNCE_TIME = 2000; // 2 seconds

// TypeScript interface for our image cache
interface EkkoImageCache {
  loadedImages: Set<string>;
  failedImages: Set<string>;
  lastUsed: Map<string, number>;
  limitSize: (maxSize?: number) => void;
}

// Add TypeScript declaration for global cache
declare global {
  interface Window {
    ekkoImageCache?: EkkoImageCache;
  }
}

// Default cache size - must match value in TokenList.tsx
const DEFAULT_CACHE_SIZE = 200; // Reduced from 300 to 200 for memory optimization

// Get or create the global image cache
if (!window.ekkoImageCache) {
  window.ekkoImageCache = {
    loadedImages: new Set<string>(),
    failedImages: new Set<string>(),
    lastUsed: new Map<string, number>(),
    
    // Method to limit cache size using LRU policy
    limitSize: function(maxSize = DEFAULT_CACHE_SIZE) {
      const cache = window.ekkoImageCache as EkkoImageCache;
      
      // If cache is under the limit, do nothing
      if (cache.loadedImages.size <= maxSize) return;
      
      // Calculate how many images to remove (just the excess)
      const numberToRemove = cache.loadedImages.size - maxSize;
    
      
      // Convert to array for sorting
      const entries = Array.from(cache.lastUsed.entries());
      
      // Sort by last used timestamp (oldest first)
      entries.sort((a, b) => a[1] - b[1]);
      
      // Remove oldest entries until we're under the limit - only remove what's needed
      const entriesToRemove = entries.slice(0, numberToRemove);
      
      // Remove from both caches
      entriesToRemove.forEach(([url, _]) => {
        cache.loadedImages.delete(url);
        cache.lastUsed.delete(url);
      });
    }
  };
}

// Get references to the cache
const imageCache = window.ekkoImageCache as EkkoImageCache;
const loadedImageCache = imageCache.loadedImages;
const failedImageCache = imageCache.failedImages;

interface TokenCardProps {
  token: Token;
  onSelect?: (token: Token) => void;
  highlight?: boolean;
  category?: 'new' | 'trending' | 'migrated' | 'graduating' | 'graduated'; // Add category prop
}

/**
 * Optimize image URL for direct use
 * @param imageUrl The original image URL
 * @returns Optimized image URL
 */
function optimizeImageUrl(imageUrl: string | null | undefined): string {
  if (!imageUrl) {
    return PLACEHOLDER_IMAGE;
  }

  // Check if this image previously failed to load
  if (failedImageCache.has(imageUrl)) {
    return PLACEHOLDER_IMAGE;
  }

  // List of problematic domains known to cause CORS issues
  const problematicDomains = ['moonitcdn.io', 'cf-ipfs.com', 'ipfs.dweb.link', 'ipfs.moralis.io'];
  if (problematicDomains.some(domain => imageUrl.includes(domain))) {
    return PLACEHOLDER_IMAGE;
  }
  
  // For Supabase URLs, use them directly without transformations
  // This ensures fastest loading from Supabase's CDN
  if (imageUrl.includes('supabase.co') && imageUrl.includes('storage/v1')) {
    return imageUrl;
  }
  
  try {
    // Verify this is a valid URL format
    new URL(imageUrl);
    
    // Skip any data URLs that might be problematic
    if (imageUrl.startsWith('data:') && !imageUrl.startsWith('data:image/svg+xml')) {
      // Keep only SVG data URLs which are more reliable
      return PLACEHOLDER_IMAGE;
    }
    
    // Check if image is already loaded or loading
    if (loadedImageCache.has(imageUrl)) {
      return imageUrl;
    }
    
    return imageUrl;
  } catch (e) {
    return PLACEHOLDER_IMAGE;
  }
}

// Get stable image URL that won't change between renders
const getStableImageUrl = (token: Token): string => {
  // Use a memoized value to avoid recalculating on every render
  const imageSource = token.image_url || token.image_uri;
  
  const imageDebug = {
    source: imageSource ? 'Yes' : 'No',
    image_url: token.image_url ? token.image_url.substring(0, 30) + '...' : 'None',
    image_uri: token.image_uri ? token.image_uri.substring(0, 30) + '...' : 'None'
  };
  
  if (!imageSource) return PLACEHOLDER_IMAGE;
  
  // Skip image URLs that look suspicious (like those starting with '/')
  if (imageSource.startsWith('/') || imageSource === 'null' || imageSource === 'undefined') {
    return PLACEHOLDER_IMAGE;
  }
  
  // If the image is already in our cache (either loaded or failed), return the cached decision
  if (loadedImageCache.has(imageSource)) {
    // Update last used time
    imageCache.lastUsed.set(imageSource, Date.now());
    return imageSource;
  }
  
  if (failedImageCache.has(imageSource)) {
    return PLACEHOLDER_IMAGE;
  }
  
  // Otherwise optimize the URL
  return optimizeImageUrl(imageSource);
};

// Create a new component for the QuickBuy popup
const QuickBuySettings = ({ 
  isOpen, 
  onClose, 
  onSelectAmount, 
  defaultAmount,
  token
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSelectAmount: (amount: number) => void; 
  defaultAmount: number;
  token: Token;
}) => {
  const [amount, setAmount] = useState<number>(defaultAmount);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [slippage, setSlippage] = useState<string>('20'); // Default 20% slippage
  const [customSlippage, setCustomSlippage] = useState<string>('');
  const [showCustomSlippage, setShowCustomSlippage] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  
  const predefinedAmounts = [0.01, 0.05, 0.1, 0.5, 1, 5, 10];
  const predefinedSlippages = ['10', '20', '50']; // Predefined slippage percentages

  // Listen for global slippage change events
  useEffect(() => {
    const handleGlobalSlippageChange = (event: CustomEvent<{slippage: string}>) => {
      const { slippage: newSlippage } = event.detail;
      setSlippage(newSlippage);
    };
    
    // Add event listener when component mounts
    window.addEventListener('ekko:slippage_changed', handleGlobalSlippageChange as EventListener);
    
    // Clean up when component unmounts
    return () => {
      window.removeEventListener('ekko:slippage_changed', handleGlobalSlippageChange as EventListener);
    };
  }, []);

  useEffect(() => {
    // Load saved preferences when the modal opens
    if (isOpen) {
      setCustomAmount('');
      
      // Load preferences from multiple sources (priority order: Supabase > localStorage > defaults)
      const loadUserPreferences = async () => {
        try {
          // First try to get wallet address
          const walletAddress = window.solana?.publicKey?.toString() || 
                              window.solflare?.publicKey?.toString();
          
          let loadedAmount = defaultAmount;
          let loadedSlippage = '20';
          
          if (walletAddress) {
            // Try to load from Supabase user preferences
            try {
              const { userService } = await import('../services/userService');
              const user = await userService.getUserByWallet(walletAddress);
              
              if (user?.user_preferences) {
                const prefs = user.user_preferences;
                
                // Load amount from user preferences
                if (prefs.quickBuyAmount) {
                  const savedAmount = parseFloat(prefs.quickBuyAmount);
                  if (!isNaN(savedAmount) && savedAmount > 0) {
                    loadedAmount = savedAmount;
                    console.log(`✅ Loaded amount from Supabase: ${loadedAmount} SOL`);
                  }
                }
                
                // Load slippage from user preferences (check both fields)
                if (prefs.quickBuySlippage) {
                  loadedSlippage = prefs.quickBuySlippage.toString();
                  console.log(`✅ Loaded slippage from Supabase: ${loadedSlippage}%`);
                } else if (prefs.tradeSlippage) {
                  loadedSlippage = prefs.tradeSlippage.toString();
                  console.log(`✅ Loaded slippage from Supabase (tradeSlippage): ${loadedSlippage}%`);
                }
              }
            } catch (error) {
              console.warn('Could not load user preferences from Supabase:', error);
            }
          }
          
          // Fallback to localStorage if Supabase didn't have values
          if (loadedAmount === defaultAmount) {
            const savedAmount = localStorage.getItem('ekko_quick_buy_amount');
            if (savedAmount) {
              const parsedAmount = parseFloat(savedAmount);
              if (!isNaN(parsedAmount) && parsedAmount > 0) {
                loadedAmount = parsedAmount;
                console.log(`📦 Loaded amount from localStorage: ${loadedAmount} SOL`);
              }
            }
          }
          
          if (loadedSlippage === '20') {
            const savedSlippage = localStorage.getItem('ekko_quick_buy_slippage');
            if (savedSlippage) {
              loadedSlippage = savedSlippage;
              console.log(`📦 Loaded slippage from localStorage: ${loadedSlippage}%`);
            }
          }
          
          // Apply the loaded values
          setAmount(loadedAmount);
          setSlippage(loadedSlippage);
          
          console.log(`🎯 Final loaded preferences: ${loadedAmount} SOL, ${loadedSlippage}% slippage`);
          
        } catch (error) {
          console.error('Error loading user preferences:', error);
          // Use defaults on error
          setAmount(defaultAmount);
          setSlippage('20');
        }
      };
      
      loadUserPreferences();
    }
  }, [isOpen, defaultAmount]);

  const handleSelectAmount = (selectedAmount: number) => {
    setAmount(selectedAmount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and decimals
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
    
    // Convert to number if valid
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setAmount(numValue);
    }
  };
  
  // Function to broadcast slippage changes to other components
  const broadcastSlippageChange = (newSlippage: string) => {
    // Dispatch a custom event that other components can listen for
    window.dispatchEvent(new CustomEvent('ekko:slippage_changed', {
      detail: { slippage: newSlippage }
    }));
  };
  
  const handleSelectSlippage = (selectedSlippage: string) => {
    setSlippage(selectedSlippage);
    setShowCustomSlippage(false);
    setCustomSlippage('');
    
    // Broadcast this change to other components
    broadcastSlippageChange(selectedSlippage);
  };
  
  const handleCustomSlippageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and decimals
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomSlippage(value);
    
    // Convert to number to validate
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0 && numValue <= 100) {
      setSlippage(value);
    }
  };
  
  const handleCustomSlippageSubmit = () => {
    // Validate slippage value
    const numValue = parseFloat(customSlippage);
    if (!isNaN(numValue) && numValue > 0 && numValue <= 100) {
      setSlippage(numValue.toString());
      setShowCustomSlippage(false);
      
      // Broadcast this change to other components
      broadcastSlippageChange(numValue.toString());
    } else {
      toast.error('Slippage must be between 0.1 and 100%');
    }
  };

  const handleSavePreference = () => {
    try {
      setSaving(true);
      
      // Store the preferences in localStorage
      localStorage.setItem('ekko_quick_buy_amount', amount.toString());
      localStorage.setItem('ekko_quick_buy_slippage', slippage.toString());
      
      // Also update the quickBuyService settings
      if (typeof window !== 'undefined') {
        // Create or update user preferences object in localStorage
        const userPrefs = JSON.parse(localStorage.getItem('ekko_user_preferences') || '{}');
        userPrefs.quickBuyAmount = amount.toString();
        userPrefs.quickBuySlippage = slippage.toString();
        localStorage.setItem('ekko_user_preferences', JSON.stringify(userPrefs));
      }
      
      // Save to Supabase if wallet is connected
      const walletAddress = window.solana?.publicKey?.toString() || 
                          window.solflare?.publicKey?.toString();
      
      if (walletAddress) {
        // Import dynamically to avoid circular dependencies
        import('../services/userService').then(({userService}) => {
          userService.getUserByWallet(walletAddress).then(user => {
            if (user && user.id) {
              // Get existing preferences or create new object
              const userPrefs = user.user_preferences || {};
              
              // Update with new values - sync both slippage fields
              userPrefs.quickBuyAmount = amount.toString();
              userPrefs.quickBuySlippage = slippage.toString();
              userPrefs.tradeSlippage = slippage.toString(); // Keep both slippage fields in sync
              
              // Save to Supabase
              userService.updateUserPreferences(user.id, userPrefs)
                .then(() => {
                })
                .catch(err => {
                });
            }
          });
        });
      }
      
      // Broadcast slippage change to ensure all components are in sync
      broadcastSlippageChange(slippage);
      
      // Show success toast with slippage information
      toast.success(`Global quick buy settings saved! Amount: ${amount} SOL, Slippage: ${slippage}%`);
      
      // Pass the selected amount back
      onSelectAmount(amount);
      onClose();
    } catch (error) {
      console.error('Error saving preference:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Use createPortal to render the popup outside of the TokenCard's DOM hierarchy
  return createPortal(
    <div 
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white border border-gray-200 rounded-xl shadow-2xl max-w-sm w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with mint green accent */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
              <h3 className="text-lg font-bold text-black">Quick Buy Settings</h3>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-black transition-colors">
              <X size={20} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Global settings for all quick buy buttons</p>
        </div>
        
        <div className="p-4 bg-gray-50">
          {/* Amount Settings */}
          <div className="mb-6">
            <div className="text-sm text-black mb-3 font-medium flex items-center">
              <DollarSign size={16} className="mr-2 text-[#24c35b]" />
              SOL Purchase Amount
            </div>
            
            <div className="grid grid-cols-4 gap-2 mb-3">
              {predefinedAmounts.map((predefinedAmount) => (
                <button
                  key={predefinedAmount}
                  className={`text-sm py-2.5 px-2 rounded-lg font-medium transition-colors ${
                    amount === predefinedAmount ? 
                    'bg-[#24c35b] text-white shadow-md' : 
                    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSelectAmount(predefinedAmount)}
                >
                  {predefinedAmount}
                </button>
              ))}
            </div>
            
            <div className="mb-2">
              <div className="flex items-center">
                <input
                  type="text"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Custom amount"
                  className="bg-white border border-gray-300 rounded-l-lg py-2.5 px-3 text-black text-sm w-full focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                />
                <div className="bg-gray-200 border border-l-0 border-gray-300 rounded-r-lg py-2.5 px-3 text-sm text-gray-600 font-medium">
                  SOL
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">This amount applies to all quick buy buttons</p>
          </div>
          
          {/* Slippage Settings */}
          <div className="mb-6">
            <div className="text-sm text-black mb-3 font-medium flex items-center">
              <Info size={16} className="mr-2 text-[#24c35b]" />
              Slippage Tolerance
            </div>
            
            <div className="grid grid-cols-3 gap-2 mb-3">
              {predefinedSlippages.map((predefinedSlippage) => (
                <button
                  key={predefinedSlippage}
                  className={`text-sm py-2.5 px-2 rounded-lg font-medium transition-colors ${
                    slippage === predefinedSlippage ? 
                    'bg-[#24c35b] text-white shadow-md' : 
                    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSelectSlippage(predefinedSlippage)}
                >
                  {predefinedSlippage}%
                </button>
              ))}
            </div>
            
            {!showCustomSlippage ? (
              <button
                className="w-full text-sm py-2.5 px-3 rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors font-medium"
                onClick={() => setShowCustomSlippage(true)}
              >
                Custom Slippage
              </button>
            ) : (
              <div className="mb-2">
                <div className="flex items-center">
                  <input
                    type="text"
                    value={customSlippage}
                    onChange={handleCustomSlippageChange}
                    placeholder="Custom %"
                    className="bg-white border border-gray-300 rounded-l-lg py-2.5 px-3 text-black text-sm flex-grow focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                  />
                  <button
                    onClick={handleCustomSlippageSubmit}
                    className="bg-[#24c35b] text-white text-sm py-2.5 px-4 rounded-r-lg font-medium hover:bg-[#1ea348] transition-colors"
                  >
                    Set
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Higher slippage increases success chance but may result in worse price
                </p>
              </div>
            )}
          </div>
          
          {/* Summary */}
          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="text-center text-sm text-black mb-2 font-medium">
              Current Settings
            </div>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-700">
              <div className="flex items-center">
                <span className="text-[#24c35b] font-bold">{amount}</span> 
                <span className="ml-1">SOL</span>
              </div>
              <span className="text-gray-400">•</span>
              <div className="flex items-center">
                <span className="text-[#24c35b] font-bold">{slippage}%</span>
                <span className="ml-1">slippage</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer with actions */}
        <div className="p-4 border-t border-gray-200 bg-white flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 text-sm bg-gray-200 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSavePreference}
            className="flex-1 text-sm bg-[#24c35b] text-white font-medium py-2.5 px-4 rounded-lg hover:bg-[#1ea348] transition-colors flex items-center justify-center"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader size={14} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body  // Render the modal directly in the document body
  );
};

// Update the processSocialLinks function to use metadataService for fetching URI data
const processSocialLinks = (token: Token) => {
  const links: {
    twitter: string | null;
    telegram: string | null;
    website: string | null;
    youtube: string | null;
    discord: string | null;
  } = {
    twitter: null,
    telegram: null,
    website: null,
    youtube: null,
    discord: null
  };

  // Helper to identify link type
  const identifyLinkType = (url: string) => {
    if (!url || typeof url !== 'string') return null;
    
    // Clean the URL
    let cleanUrl = url.trim();
    if (!cleanUrl) return null;
    
    // Remove common invalid characters and patterns
    cleanUrl = cleanUrl.replace(/[<>"\s]/g, '');
    
    // Skip obviously invalid URLs
    if (cleanUrl.length < 4 || 
        cleanUrl.includes('null') || 
        cleanUrl.includes('undefined') ||
        cleanUrl === 'N/A' ||
        cleanUrl === 'n/a') {
      return null;
    }
    
    // Skip image URLs - these should NOT be treated as websites
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp', '.ico'];
    const lowerUrl = cleanUrl.toLowerCase();
    if (imageExtensions.some(ext => lowerUrl.includes(ext))) {
      return null; // Don't treat images as any type of social link
    }
    
    // Skip other media/file URLs
    const mediaExtensions = ['.mp4', '.mp3', '.pdf', '.doc', '.json', '.xml'];
    if (mediaExtensions.some(ext => lowerUrl.includes(ext))) {
      return null;
    }
    
    // Add https:// if missing (but only for valid-looking URLs)
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      // Only add https if it looks like a valid domain
      if (cleanUrl.includes('.') && !cleanUrl.includes('@') && !cleanUrl.includes(' ')) {
        cleanUrl = 'https://' + cleanUrl;
      } else {
        // For things like @username, don't add https
        cleanUrl = cleanUrl;
      }
    }
    
    try {
      // Validate URL format before proceeding
      if (cleanUrl.startsWith('http')) {
        new URL(cleanUrl); // This will throw if invalid
      }
      
      // Check for known patterns
      if (cleanUrl.includes('twitter.com') || cleanUrl.includes('x.com') || 
          cleanUrl.includes('@') || cleanUrl.match(/\bx\.com\b/)) {
        // Format Twitter URLs properly
        if (cleanUrl.includes('@') && !cleanUrl.includes('twitter.com') && !cleanUrl.includes('x.com')) {
          const username = cleanUrl.replace('@', '');
          cleanUrl = `https://x.com/${username}`;
        }
        return { type: 'twitter', url: cleanUrl };
      }
      
      if (cleanUrl.includes('t.me') || cleanUrl.includes('telegram.me') || 
          cleanUrl.includes('telegram.org') || cleanUrl.includes('telegram')) {
        // Format telegram links properly
        if (!cleanUrl.includes('.') && !cleanUrl.includes('/')) {
          cleanUrl = `https://t.me/${cleanUrl.replace('@', '')}`;
        } else if (!cleanUrl.includes('t.me/')) {
          const username = cleanUrl.split('/').pop()?.replace('@', '') || '';
          if (username) {
            cleanUrl = `https://t.me/${username}`;
          }
        }
        return { type: 'telegram', url: cleanUrl };
      }
      
      if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
        return { type: 'youtube', url: cleanUrl };
      }
      
      if (cleanUrl.includes('discord.com') || cleanUrl.includes('discord.gg')) {
        return { type: 'discord', url: cleanUrl };
      }
      
      // For websites, be very specific - only actual website domains
      if (cleanUrl.startsWith('http') && cleanUrl.includes('.')) {
        const urlObj = new URL(cleanUrl);
        
        // Additional checks to ensure this is actually a website, not metadata/API endpoints
        const hostname = urlObj.hostname.toLowerCase();
        
        // Skip known non-website domains
        const nonWebsiteDomains = [
          'metadata.boop.fun',
          'arweave.net',
          'ipfs.io',
          'gateway.pinata.cloud',
          'cf-ipfs.com',
          'api.',
          'cdn.',
          'storage.',
          'amazonaws.com',
          'googleapis.com',
          'supabase.co'
        ];
        
        if (nonWebsiteDomains.some(domain => hostname.includes(domain))) {
          return null; // Skip metadata/storage/API domains
        }
        
        // Skip paths that look like metadata or file endpoints
        const pathname = urlObj.pathname.toLowerCase();
        if (pathname.includes('/metadata') || 
            pathname.includes('/api/') ||
            pathname.includes('/storage/') ||
            pathname.includes('.json') ||
            imageExtensions.some(ext => pathname.includes(ext))) {
          return null;
        }
        
        // Only treat as website if it passes all checks
        if (urlObj.hostname && 
            urlObj.hostname.includes('.') && 
            !urlObj.hostname.includes(' ') &&
            urlObj.hostname.length > 3) {
          return { type: 'website', url: cleanUrl };
        }
      }
    } catch (error) {
      // URL is invalid, skip it
      console.debug(`Invalid URL format: ${cleanUrl}`);
      return null;
    }
    
    return null;
  };

  // First collect all standard fields that might contain links
  const standardFields: (keyof Token)[] = [
    'twitter', 'telegram', 'website', 'youtube', 'discord'
  ];
  
  // Scan all these fields for potential links
  standardFields.forEach(field => {
    const value = token[field];
    if (value && typeof value === 'string') {
      const linkInfo = identifyLinkType(value);
      if (linkInfo) {
        // Set the link in the corresponding category based on content, not field name
        links[linkInfo.type as keyof typeof links] = linkInfo.url;
      }
    }
  });

  // Scan metadata object if present
  if (token.metadata) {
    // Standard fields in metadata
    const metadataFields = ['twitter', 'telegram', 'website', 'youtube', 'discord', 'url', 'external_url'];
    
    metadataFields.forEach(field => {
      if (token.metadata && token.metadata[field]) {
        const linkInfo = identifyLinkType(token.metadata[field]);
        if (linkInfo) {
          links[linkInfo.type as keyof typeof links] = linkInfo.url;
        }
      }
    });
    
    // Look inside metadata.properties if it exists
    if (token.metadata.properties) {
      Object.entries(token.metadata.properties).forEach(([key, value]) => {
        if (typeof value === 'string') {
          const linkInfo = identifyLinkType(value);
          if (linkInfo) {
            links[linkInfo.type as keyof typeof links] = linkInfo.url;
          }
        }
      });
    }
    
    // Scan description for URLs
    if (token.metadata.description && typeof token.metadata.description === 'string') {
      // Look for URL patterns in description
      const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|(\w+\.(com|org|io|net|xyz|app)[^\s]*)/g;
      const matches = token.metadata.description.match(urlRegex);
      
      if (matches) {
        matches.forEach((url: string) => {
          const linkInfo = identifyLinkType(url);
          if (linkInfo) {
            links[linkInfo.type as keyof typeof links] = linkInfo.url;
          }
        });
      }
    }
  }

  // If token has description, scan it too
  if (token.description && typeof token.description === 'string') {
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|(\w+\.(com|org|io|net|xyz|app)[^\s]*)/g;
    const matches = token.description.match(urlRegex);
    
    if (matches) {
      matches.forEach((url: string) => {
        const linkInfo = identifyLinkType(url);
        if (linkInfo) {
          links[linkInfo.type as keyof typeof links] = linkInfo.url;
        }
      });
    }
  }

  // Last resort: Check every string property of the token for potential links
  for (const [key, value] of Object.entries(token)) {
    // Skip properties we've already checked
    if (standardFields.includes(key as any) || 
        key === 'metadata' || 
        key === 'description') {
      continue;
    }
    
    if (typeof value === 'string' && value.length > 5) {
      const linkInfo = identifyLinkType(value);
      if (linkInfo) {
        links[linkInfo.type as keyof typeof links] = linkInfo.url;
      }
    }
  }

  return links;
};

// Add a utility function to check if token is in any active feed
const isTokenInActiveFeeds = (tokenMint: string): boolean => {
  try {
    // Check localStorage for token feed membership
    const newTokens = JSON.parse(localStorage.getItem('ekko_new_tokens') || '[]');
    const trendingTokens = JSON.parse(localStorage.getItem('ekko_trending_tokens') || '[]');
    const migratedTokens = JSON.parse(localStorage.getItem('ekko_migrated_tokens') || '[]');
    
    // Check if token is in any of these lists
    return newTokens.includes(tokenMint) || 
           trendingTokens.includes(tokenMint) || 
           migratedTokens.includes(tokenMint);
  } catch (error) {
    console.error('TokenCard: Error checking feed membership:', error);
    // If we can't check, default to allowing subscription
    return true;
  }
};

const TokenCardComponent: React.FC<TokenCardProps> = ({ token, onSelect, highlight = false, category = 'new' }) => {
  // Process and normalize all social links
  const [socialLinks, setSocialLinks] = useState(() => processSocialLinks(token));
  
  // Add state for URI metadata
  const [uriMetadata, setUriMetadata] = useState<TokenMetadata | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  // Add time-related state at the component level (moved from getTimeSince)
  const [timeSince, setTimeSince] = useState<string>(() => calculateTimeSince(token.received_at || token.discovery_timestamp || token.created_at || token.timestamp || null));
  
  // Update time periodically
  useEffect(() => {
    const updateTime = () => {
      setTimeSince(calculateTimeSince(token.created_at || token.timestamp || null));
    };
    
    // Update time every second
    const interval = setInterval(updateTime, 1000);
    
    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [token.created_at, token.timestamp]);
  
  // Fetch metadata from URI when token changes
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!token.uri) return;
      
      try {
        setIsLoadingMetadata(true);
        const metadata = await metadataService.fetchTokenMetadata(token.uri);
        setUriMetadata(metadata);
        
        if (metadata) {
          
          // Get an updated social links object that includes metadata
          const updatedSocialLinks = {
            ...socialLinks,
            twitter: metadata.twitter || socialLinks.twitter,
            telegram: metadata.telegram || socialLinks.telegram,
            website: metadata.website || socialLinks.website,
            youtube: metadata.youtube || socialLinks.youtube,
            discord: metadata.discord || socialLinks.discord
          };
          
          setSocialLinks(updatedSocialLinks);
        }
      } catch (error: any) {
        // Metadata is not critical - fail silently
        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
          console.debug(`Metadata fetch failed for ${token.symbol || token.mint}:`, error?.message || 'Unknown error');
        }
      } finally {
        setIsLoadingMetadata(false);
      }
    };
    
    fetchMetadata();
  }, [token.uri]);

  // Update social links when token changes
  useEffect(() => {
    // Only update for token changes, metadata updates are handled separately
    const processedLinks = processSocialLinks(token);
    
    // Don't overwrite with null values if we have uriMetadata
    if (uriMetadata) {
      const mergedLinks = {
        twitter: uriMetadata.twitter || processedLinks.twitter,
        telegram: uriMetadata.telegram || processedLinks.telegram,
        website: uriMetadata.website || processedLinks.website,
        youtube: uriMetadata.youtube || processedLinks.youtube,
        discord: uriMetadata.discord || processedLinks.discord
      };
      setSocialLinks(mergedLinks);
    } else {
      setSocialLinks(processedLinks);
    }
  }, [token, uriMetadata]);
  
  // Create a state to handle animation
  const [isHighlighted, setIsHighlighted] = useState(highlight);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  
  // Add state for quick buy settings
  const [showQuickBuySettings, setShowQuickBuySettings] = useState(false);
  const [quickBuyAmount, setQuickBuyAmount] = useState<number>(0.1); // Default amount
  const [quickBuySlippage, setQuickBuySlippage] = useState<string>('20'); // Default 20% slippage
  
  // Use memo to get a stable image URL that won't change between renders
  // This is critical to avoid unnecessary re-renders
  const [imageUrl, setImageUrl] = useState<string>(() => {
    return getStableImageUrl(token);
  });
  
  const [copied, setCopied] = useState(false);
  // Create a ref for the last click time
  const lastClickTimeRef = useRef<number>(0);
  
  // Cache the token's mint for stability checks
  const tokenMintRef = useRef(token.mint);
  const imageSourceRef = useRef(token.image_url || token.image_uri);
  
  // Initialize quick buy settings from localStorage and user preferences
  useEffect(() => {
    // First try to get from localStorage cache for immediate display
    try {
      const cachedAmount = getCachedQuickBuyAmount();
      setQuickBuyAmount(cachedAmount);
    } catch (error) {
      console.error('Error getting cached quick buy amount:', error);
    }
    
    // Then try to get the user's saved preferences (may take a moment)
    const getUserQuickBuyPreferences = async () => {
      try {
        if (window.solana && window.solana.publicKey) {
          const walletAddress = window.solana.publicKey.toString();
          const userData = await userService.getUserByWallet(walletAddress);
          
          if (userData?.user_preferences?.quickBuyAmount) {
            const savedAmount = parseFloat(userData.user_preferences.quickBuyAmount);
            if (!isNaN(savedAmount)) {
              setQuickBuyAmount(savedAmount);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user quick buy preferences:', error);
      }
    };
    
    getUserQuickBuyPreferences();
  }, []);

  // Listen for global quick buy amount changes
  useEffect(() => {
    const handleQuickBuyAmountChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{amount: number}>;
      setQuickBuyAmount(customEvent.detail.amount);
    };

    window.addEventListener('ekko:quick_buy_amount_changed', handleQuickBuyAmountChanged);
    
    return () => {
      window.removeEventListener('ekko:quick_buy_amount_changed', handleQuickBuyAmountChanged);
    };
  }, []);

  // Update highlighted state when prop changes
  useEffect(() => {
    if (highlight) {
      setIsHighlighted(true);
      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [highlight]);

  // Update image URL only when the token's image actually changes
  useEffect(() => {
    // Only process if the token's mint or image source has actually changed
    const currentImageSource = token.image_url || token.image_uri;
    
    
    // If we don't have a current image source, but do have an existing image URL that isn't the placeholder,
    // then don't change it (this preserves good images during updates)
    if (!currentImageSource && imageUrl && imageUrl !== PLACEHOLDER_IMAGE) {
      return;
    }
    
    // Check if we need to update the image
    if (
      token.mint !== tokenMintRef.current || 
      currentImageSource !== imageSourceRef.current ||
      (imageUrl === PLACEHOLDER_IMAGE && currentImageSource) // Force update if we have a source but are showing placeholder
    ) {
      // Update refs
      tokenMintRef.current = token.mint;
      imageSourceRef.current = currentImageSource;
      
      // Get stable URL for the new image
      const newImageUrl = getStableImageUrl(token);
      
      // Only update state if URL actually changes to avoid re-rendering cycles
      if (newImageUrl !== imageUrl) {
        // If not already in cache and not a placeholder, preload the image
        if (!loadedImageCache.has(newImageUrl) && newImageUrl !== PLACEHOLDER_IMAGE) {
          // Preload the image before updating state to avoid flickering
          const img = new Image();
          img.onload = () => {
            loadedImageCache.add(newImageUrl);
            // Track last used time
            imageCache.lastUsed.set(newImageUrl, Date.now());
            setImageUrl(newImageUrl);
            setImageLoaded(true);
          };
          img.onerror = () => {
            // Add to failed cache
            failedImageCache.add(newImageUrl);
            setImageUrl(PLACEHOLDER_IMAGE);
            setImageLoaded(true);
          };
          img.src = newImageUrl;
        } else {
          // If already in cache, just update the state and last used time
          if (newImageUrl !== PLACEHOLDER_IMAGE) {
            imageCache.lastUsed.set(newImageUrl, Date.now());
          }
          setImageUrl(newImageUrl);
          setImageLoaded(true);
        }
      }
    }
  }, [token]);

  // Make sure the cache doesn't grow too large - run this less frequently
  useEffect(() => {
    // Limit cache size periodically
    const cleanupTimer = setInterval(() => {
      try {
        // Only do a cache check if we're over the threshold to avoid unnecessary trimming
        if (loadedImageCache.size > DEFAULT_CACHE_SIZE * 0.9) { // Only trim when near limit
          imageCache.limitSize(DEFAULT_CACHE_SIZE);
        }
      } catch (error) {
        console.error('Error in cache cleanup timer:', error);
      }
    }, 300000); // Check every 5 minutes instead of every minute
    
    return () => clearInterval(cleanupTimer);
  }, []);

  // Format numbers with K, M suffixes
  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(1);
  };

  // Format SOL amount with appropriate precision
  const formatSol = (sol: number | null) => {
    if (sol === null) return 'N/A';
    if (sol < 0.001 && sol !== 0.001) return `<0.001`;
    if (sol === 0.001) return `0.001`;
    if (sol < 1) return `${sol.toFixed(3)}`;
    return `${sol.toFixed(2)}`;
  };

  // Replace the problematic function with one that doesn't use hooks
  const getTimeSince = () => timeSince;

  // Calculate transaction count
  const getTxCount = () => {
    // Properly handle the case where counts are 0 but valid
    const buys = token.buys_count !== undefined && token.buys_count !== null 
      ? token.buys_count 
      : (token.buys !== undefined && token.buys !== null ? token.buys : 0);
    
    const sells = token.sells_count !== undefined && token.sells_count !== null 
      ? token.sells_count 
      : (token.sells !== undefined && token.sells !== null ? token.sells : 0);
      
    return buys + sells;
  };
  
  // Update the token transaction event listener to check feed membership
  useEffect(() => {
    // Handler for token transaction events
    const handleTokenTransaction = (event: Event) => {
      try {
        const customEvent = event as CustomEvent<{tokenMint: string, transaction: any, timestamp: number}>;
        const { tokenMint, transaction } = customEvent.detail;
        
        // Only process if this transaction is for our token
        // AND the token is in an active feed (to respect feed membership)
        if (tokenMint === token.mint && isTokenInActiveFeeds(token.mint)) {
          console.log(`TokenCard received transaction for ${token.symbol || token.mint}:`, transaction);
          
          // Check if the transaction is valid
          if (!transaction || !transaction.type) return;
          
          // Update buy/sell counts based on transaction type
          if (transaction.type.toLowerCase() === 'buy') {
            // Update buyCount
            setBuyCount(prevCount => prevCount + 1);
          } else if (transaction.type.toLowerCase() === 'sell') {
            // Update sellCount
            setSellCount(prevCount => prevCount + 1);
          }
          
          // Flash highlight effect to show the card was updated
          setIsHighlighted(true);
          setTimeout(() => setIsHighlighted(false), 2000);
        }
      } catch (error) {
        console.error('Error handling token transaction event:', error);
      }
    };
    
    // Add transaction event listener
    window.addEventListener('ekko:token_transaction', handleTokenTransaction as EventListener);
    
    // Cleanup listener when component unmounts
    return () => {
      window.removeEventListener('ekko:token_transaction', handleTokenTransaction as EventListener);
    };
  }, [token.mint]); // Only re-register if token.mint changes

  // Add state for tracking buys/sells directly in the component
  const [buyCount, setBuyCount] = useState(() => {
    return typeof token.buys_count === 'number' ? token.buys_count : 0;
  });

  const [sellCount, setSellCount] = useState(() => {
    return typeof token.sells_count === 'number' ? token.sells_count : 0;
  });

  // Update local state when token changes
  useEffect(() => {
    // Update buyCount if token's count has changed
    if (typeof token.buys_count === 'number') {
      setBuyCount(token.buys_count);
    }
    
    // Update sellCount if token's count has changed
    if (typeof token.sells_count === 'number') {
      setSellCount(token.sells_count);
    }
  }, [token]);

  // 🔥 **REAL-TIME FIX**: Read directly from token props instead of state for immediate updates
  const getBuyCount = () => {
    // Read directly from token props to get real-time updates from Redis
    return token.buys_count !== undefined && token.buys_count !== null 
      ? token.buys_count 
      : (token.buys !== undefined && token.buys !== null ? token.buys : 0);
  };

  // 🔥 **REAL-TIME FIX**: Read directly from token props instead of state for immediate updates  
  const getSellCount = () => {
    // Read directly from token props to get real-time updates from Redis
    return token.sells_count !== undefined && token.sells_count !== null 
      ? token.sells_count 
      : (token.sells !== undefined && token.sells !== null ? token.sells : 0);
  };

  // Get volume safely with value preservation
  const getVolume = () => {
    // Static variable to keep the highest value we've seen
    if (!getVolume.highestValue) getVolume.highestValue = 0;
    
    // Get the current value from the token
    let currentValue = 0;
    if (typeof token.tx_volume === 'number') currentValue = token.tx_volume;
    else if (typeof token.volume_usd === 'number') currentValue = token.volume_usd;
    else if (typeof token.tx_volume === 'string') {
      const parsed = parseFloat(token.tx_volume);
      if (!isNaN(parsed)) currentValue = parsed;
    }
    else if (typeof token.volume_usd === 'string') {
      const parsed = parseFloat(token.volume_usd);
      if (!isNaN(parsed)) currentValue = parsed;
    }
    
    // Update the highest value if the current is higher
    if (currentValue > getVolume.highestValue) {
      getVolume.highestValue = currentValue;
    }
    
    // Return the highest value we've seen (preserves non-zero even if current is zero)
    return getVolume.highestValue;
  };
  // Add highestValue property to function
  getVolume.highestValue = 0;
  
  // Get market cap safely
  const getMarketCap = () => {
    if (token.market_cap_usd !== undefined && token.market_cap_usd !== null) {
      return formatUSD(token.market_cap_usd);
    }
    return 'N/A';
  };
  
  // Format percentages
  const formatPercentage = (value: number | null) => {
    if (value === null) return '0%';
    return `${value.toFixed(0)}%`;
  };
  
  // Function to copy mint address to clipboard
  const copyToClipboard = () => {
    if (token.mint) {
      navigator.clipboard.writeText(token.mint)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    }
  };
  
  // Raydium SVG Logo (inline to avoid loading issues)
  const RaydiumLogo = () => (
    <svg width="9" height="9" viewBox="0 0 108 108" xmlns="http://www.w3.org/2000/svg">
      <path d="M53.7841 0C24.0892 0 0 24.0892 0 53.7841C0 83.479 24.0892 107.568 53.7841 107.568C83.479 107.568 107.568 83.479 107.568 53.7841C107.568 24.0892 83.479 0 53.7841 0ZM53.7841 36.5132C60.461 36.5132 65.8708 41.923 65.8708 48.5999V59.0569C65.8708 65.7337 60.461 71.1435 53.7841 71.1435C47.1073 71.1435 41.6975 65.7337 41.6975 59.0569V48.5999C41.6975 41.923 47.1073 36.5132 53.7841 36.5132ZM83.7942 53.7841C83.7942 67.0355 73.0355 77.7942 59.7841 77.7942H53.7841V83.7942C67.0355 83.7942 77.7942 73.0355 77.7942 59.7841V53.7841H83.7942ZM23.7739 53.7841C23.7739 40.5327 34.5326 29.7739 47.7841 29.7739H53.7841V23.7739C40.5326 23.7739 29.7739 34.5327 29.7739 47.7841V53.7841H23.7739Z" fill="white"/>
    </svg>
  );

  // Get icon and stats
  const getIcon = () => {
    return (
      <div className="w-[60px] h-[60px] bg-dark-grey/20 rounded-lg overflow-hidden relative">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-dark-grey/50 to-black/30 flex items-center justify-center">
            <span className="text-xs font-bold text-[#24c35b]">
              {token.symbol?.substring(0, 2) || '??'}
            </span>
          </div>
        )}
        {/* Use actual image with priority loading */}
        {imageUrl !== PLACEHOLDER_IMAGE ? (
          <img 
            src={imageUrl} 
            alt={token.symbol || 'Token'} 
            className={`w-full h-full object-cover transition-opacity duration-75 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="eager" 
            onLoad={() => {
              // Add to loaded cache to avoid reloading
              loadedImageCache.add(imageUrl);
              setImageLoaded(true);
            }}
            onError={(e) => {
              // Add to failed cache
              if (imageUrl !== PLACEHOLDER_IMAGE) {
                failedImageCache.add(imageUrl);
                console.error(`Failed to load image for ${token.symbol || token.mint}: ${imageUrl}`);
              }
              setImageUrl(PLACEHOLDER_IMAGE);
              setImageLoaded(true);
            }}
          />
        ) : (
          // If we know it's a placeholder, just show the symbol
          (<div className="w-full h-full bg-[#24c35b]/10 flex items-center justify-center">
            <span className="text-xs font-bold text-[#24c35b]">
              {token.symbol?.substring(0, 2) || '??'}
            </span>
          </div>)
        )}
      </div>
    );
  };

  // Format token balance with appropriate precision
  const formatTokenBalance = (balance: string | undefined) => {
    if (!balance) return null;
    
    const numBalance = parseFloat(balance);
    if (isNaN(numBalance)) return null;
    
    if (numBalance < 0.001 && numBalance !== 0.001) return `<0.001`;
    if (numBalance === 0.001) return `0.001`;
    if (numBalance < 1) return numBalance.toFixed(3);
    if (numBalance < 10000) return numBalance.toFixed(2);
    return formatNumber(numBalance);
  };

  const userBalance = formatTokenBalance(token.userBalance);
  
  // Format USD amounts
  const formatUSD = (usd: number | null | undefined) => {
    if (usd === null || usd === undefined) return 'N/A';
    if (usd < 0.01 && usd !== 0.01) return '<$0.01';
    if (usd === 0.01) return '$0.01';
    if (usd < 1000) return `$${usd.toFixed(2)}`;
    return `$${formatNumber(usd)}`;
  };

  // Calculate the market label
  const getMarketLabel = () => {
    // First check 'market' property as requested
    if (token.market) return token.market;
    
    // Fallback to market_type
    if (token.market_type === 'raydium') return 'Raydium';
    if (token.market_type === 'jupiter') return 'Jupiter';
    if (token.market_type === 'openbook') return 'Openbook';
    if (token.market_type === 'orca') return 'Orca';
    if (token.market_type === 'pump') return 'Pump';
    return 'Unknown';
  };
  
  // Truncate name to 16 characters
  const truncateName = (name: string | null) => {
    if (!name) return 'Unknown';
    return name.length > 16 ? `${name.substring(0, 18)}...` : name;
  };

  // Function to trigger Jupiter swap directly with connected wallet
  const handleQuickBuy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    
    // If any buy button is currently being processed, ignore this click
    if (isAnyBuyButtonClicked.current) {
      return;
    }
    
    // Get current time to implement a simple debounce
    const now = Date.now();
    const lastClickTime = lastClickTimeRef.current;
    lastClickTimeRef.current = now;
    
    // Ignore clicks that happen too quickly
    if (now - lastClickTime < DEBOUNCE_TIME) {
      return;
    }
    
    // Check if we should show settings instead of buying directly
    if (e.altKey || e.ctrlKey) {
      setShowQuickBuySettings(true);
      return;
    }
    
    // Set the global buy button flag
    isAnyBuyButtonClicked.current = true;
    
    // Disable the button to prevent multiple clicks
    const button = e.currentTarget;
    button.disabled = true;
    button.classList.add('bg-gray-500');
    button.classList.remove('bg-green-500', 'hover:bg-green-600');
    
    // Get the wallet - direct use of browser wallet APIs
    let walletProvider = null;
    let walletConnected = false;
    let publicKey = null;
    
    if (window.solana) {
      walletProvider = window.solana;
      walletConnected = !!window.solana.isConnected;
      publicKey = window.solana.publicKey?.toString();
    } else if (window.solflare) {
      walletProvider = window.solflare;
      walletConnected = !!window.solflare.isConnected;
      publicKey = window.solflare.publicKey?.toString();
    }
    
    // Check if wallet is connected
    if (!walletConnected || !publicKey) {
      toast.error('Please connect your wallet to buy tokens');
      
      // Reset the button state after a delay
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove('bg-gray-500');
        button.classList.add('bg-green-500', 'hover:bg-green-600');
        isAnyBuyButtonClicked.current = false;
      }, DEBOUNCE_TIME);
      
      return;
    }

    try {
      // Show immediate feedback that the transaction is being processed
      const toastId = toast.loading("Preparing transaction...");
      
      // Execute the purchase immediately with current stored settings
      const result = await executeQuickBuy(token, publicKey, {
        amount: quickBuyAmount.toString(),
        slippage: quickBuySlippage
      });
      
      if (result.success) {
        toast.success("Transaction sent successfully", { id: toastId });
      } else {
        console.error(`[Buy] Quick buy failed: ${result.error}`);
        toast.error(result.error || "Transaction failed", { id: toastId });
      }
      
    } catch (error) {
      console.error('[Buy] Error executing quick buy:', error);
      toast.error('Error initiating purchase');
    } finally {
      // Always reset button state
      button.disabled = false;
      button.classList.remove('bg-gray-500');
      button.classList.add('bg-green-500', 'hover:bg-green-600');
      isAnyBuyButtonClicked.current = false;
    }
  };

  // Function to handle settings changes and dispatch a global event to update all components
  const updateQuickBuySettings = (amount: number) => {
    // Update local state
    setQuickBuyAmount(amount);
    
    // Immediately broadcast this amount change to all components
    // Include the current slippage value to ensure it's preserved
    window.dispatchEvent(new CustomEvent('ekko:quick_buy_amount_changed', {
      detail: { 
        amount,
        slippage: quickBuySlippage // Include current slippage to preserve it
      }
    }));
    
    // Close the settings modal
    setShowQuickBuySettings(false);
  };

  // Add a new state variable to track if the token is "new" (within 30 minutes)
  const [isNewToken, setIsNewToken] = useState<boolean>(false);

  // Add effect to check if token is "new" and should show the NEW tag
  useEffect(() => {
    const checkIfNew = () => {
      // Only show NEW tag for tokens in the 'new' category or if created recently
      if (category !== 'new' && !token.created_at) {
        setIsNewToken(false);
        return;
      }
      
      const now = Date.now();
      const createdTime = token.created_at ? new Date(token.created_at).getTime() : 0;
      const thirtyMinutesMs = 30 * 60 * 1000; // 30 minutes in milliseconds
      
      // Token is considered "new" if it was created in the last 30 minutes
      const isNew = (now - createdTime) < thirtyMinutesMs;
      setIsNewToken(isNew);
    };
    
    // Check initially
    checkIfNew();
    
    // Re-check periodically to remove NEW tag when token gets older
    const interval = setInterval(checkIfNew, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [token.created_at, category]);

  // Add a function to unsubscribe from token updates if it leaves active feeds
  const unsubscribeFromInactiveToken = () => {
    // We'll periodically check if the token is still in active feeds
    // If not, we'll stop listening for its transaction events
    const checkFeedMembership = () => {
      if (!isTokenInActiveFeeds(token.mint)) {
        // Token is no longer in any active feed, update the component state accordingly
        // This ensures stale updates don't appear for tokens that have been removed from feeds
        console.log(`TokenCard: ${token.symbol || token.mint} is no longer in active feeds`);
        // We don't need to do anything else here since event handlers already check feed membership
      }
    };
    
    // Check once
    checkFeedMembership();
    
    // Check periodically (every 5 minutes)
    const interval = setInterval(checkFeedMembership, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  };

  // Add cleanup effect
  useEffect(() => {
    const cleanup = unsubscribeFromInactiveToken();
    return cleanup;
  }, [token.mint]);

  // Render NEW tag in the component UI
  const renderNewTag = () => {
    if (isNewToken) {
      return (
        <div className="px-1.5 py-0.5 bg-[#24c35b]/20 rounded text-[#24c35b] text-[8px] font-bold animate-pulse">
          NEW
        </div>
      );
    }
    return null;
  };

  // Get bonding curve progress
  const getBondingCurveProgress = () => {
    return token.bonding_curve_progress || 0;
  };

  return (
    <div
      className={`overflow-hidden hover:border-gray-700/60 transition-colors duration-200 cursor-pointer w-full h-full relative ${
        isHighlighted ? 'bg-[#24c35b]/5' : ''
      }`}
      style={{
        backgroundColor: isHighlighted ? 'rgba(56, 205, 154, 0.1)' : 'rgb(255, 255, 255)',
        height: '90px' // Increased height to accommodate repositioned social icons
      }}
      onClick={(e) => {
        e.preventDefault();
        
        const mintString = typeof token.mint === 'string' ? token.mint : String(token.mint);
        console.log('Token Card clicked:', token.symbol || mintString, 'dispatching events...');
        
        // Forceful approach to update all canvas widgets
        // 1. Dispatch the normal token_selected event that some components listen for
        const event = new CustomEvent('ekko:token_selected', { 
          detail: { token, force_refresh: true }
        });
        window.dispatchEvent(event);
        
        // 2. Also dispatch the canvas_update_widgets event specifically for canvas
        const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', { 
          detail: { token, timestamp: Date.now() }
        });
        window.dispatchEvent(canvasUpdateEvent);
        
        // 3. Dispatch event specifically for bundle scanner
        const bundleScannerEvent = new CustomEvent('ekko:bundle_scanner_update', {
          detail: { token, timestamp: Date.now() }
        });
        window.dispatchEvent(bundleScannerEvent);
        
        console.log('✅ All events dispatched for token:', token.symbol || mintString);
        
        // 4. Call the onSelect callback if provided (for backward compatibility)
        if (onSelect) {
          onSelect(token);
        }
      }}
    >
      {/* Developer Sold Overlay - positioned just left of MC label */}
      {token.dev_sell_time && (
        <div 
          className="absolute top-0 right-20 z-10 bg-red-500 text-white text-[6px] font-bold px-1.5 py-0.5 rounded-b-md shadow-md cursor-help"
          title={`Developer Sold: ${new Date(token.dev_sell_time).toLocaleString()}`}
          onClick={(e) => e.stopPropagation()}
        >
          DS
        </div>
      )}
      {/* Background blur effect using the same image */}
      {imageUrl !== PLACEHOLDER_IMAGE && (
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center opacity-5"
          style={{
            backgroundImage: `url(${imageUrl})`,
          }}
        />
      )}
      
      {/* Main content */}
      <div className="flex flex-col h-full">
        {/* Card content - takes up available space minus bonding curve */}
        <div className="relative z-10" style={{ height: 'calc(100% - 12px)' }}>
          <div className="flex items-stretch h-full">
            {/* Left: Token Icon */}
            <div className="w-[54px] h-[54px] flex-shrink-0 p-0.5 mr-2 mt-1">
              {getIcon()}
            </div>
            
            {/* Left: Token Icon + Basic Info */}
            <div className="flex flex-col justify-between flex-grow p-1.5">
              {/* Top section with symbol, time, and controls */}
              <div className="flex items-start justify-between mb-0.5">
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-xs font-bold text-gray-900">
                      {token.symbol || 'N/A'}
                    </span>
                    <span className="text-[9px] text-[#24c35b] font-bold">
                      {getTimeSince()}
                    </span>

                    {/* Copy mint button */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard();
                      }}
                      className="p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
                      title="Copy token address"
                    >
                      {copied ? <Check size={10} className="text-[#24c35b]" /> : <Copy size={10} />}
                    </button>
                    {token.is_rugged && (
                      <AlertTriangle size={10} className="text-red-500" />
                    )}
                  </div>
                  
                  {/* Token name */}
                  <div className="mb-1">
                    <h3 className="text-[10px] font-medium truncate max-w-[120px] text-gray-800">
                      {truncateName(token.name)}
                    </h3>
                  </div>
                  
                  {/* Social links as small icons - below the name */}
                  <div className="flex gap-1 mb-1">
                    <a
                      href={`https://x.com/search?q=${token.mint}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-500 hover:text-gray-700 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center"
                      title="Search on Twitter"
                    >
                      <Search size={10} />
                    </a>
                    <a
                      href={`https://pump.fun/coin/${token.mint}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-500 hover:text-gray-700 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center"
                      title="View on Pump.fun"
                    >
                      <Pill size={10} />
                    </a>
                    {socialLinks.twitter && isValidUrl(socialLinks.twitter) && (
                      <a
                        href={String(socialLinks.twitter)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="bg-gray-100 hover:bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
                        title="Official Twitter"
                      >
                        <img 
                          src="https://cdn.cms-twdigitalassets.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.2560.png" 
                          alt="X" 
                          className="w-2.5 h-2.5"
                        />
                      </a>
                    )}
                    {socialLinks.telegram && isValidUrl(socialLinks.telegram) && (
                      <a
                        href={String(socialLinks.telegram)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-white bg-[#0088cc] rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
                        title="Official Telegram"
                      >
                        <MessageCircle size={10} />
                      </a>
                    )}
                    {socialLinks.youtube && isValidUrl(socialLinks.youtube) && (
                      <a
                        href={String(socialLinks.youtube)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-white bg-[#FF0000] rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
                        title="Official YouTube"
                      >
                        <Youtube size={10} />
                      </a>
                    )}
                    {socialLinks.website && isValidUrl(socialLinks.website) && (
                      <a
                        href={String(socialLinks.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={`text-white ${token.is_raydium ? 'bg-[#0C3EF8]' : 'bg-gray-600'} rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80`}
                        title={`Official ${token.is_raydium ? 'Raydium' : 'Website'}`}
                      >
                        {token.is_raydium ? (
                          <RaydiumLogo />
                        ) : (
                          <Globe size={10} />
                        )}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Bottom row with balance */}
              <div className="flex items-center justify-start">
                <div className="flex items-center gap-1.5">
                  {userBalance && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-[8px]">
                      <Wallet size={8} />
                      <span className={isHighlighted ? 'text-[#24c35b] font-bold' : ''}>{userBalance}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right: Stats + Price Info */}
            <div className="text-right flex-shrink-0 p-1.5">
              <div className="flex flex-col items-end h-full space-y-1">
                {/* Market Cap at the top */}
                <div className="text-xs font-bold text-[#24c35b]">
                  MC {getMarketCap()}
                </div>
                
                {/* Buy/Sell stats in the middle */}
                <div className="flex gap-1">
                  <span className="bg-green-500/20 text-green-600 rounded px-1.5 py-0.5 text-[10px] font-bold">{getBuyCount()}</span>
                  <span className="bg-red-500/20 text-red-600 rounded px-1.5 py-0.5 text-[10px] font-bold">{getSellCount()}</span>
                </div>
                
                {/* Buy/Settings Box - horizontal layout */}
                <div className="flex w-16 h-10 rounded-lg overflow-hidden shadow-lg border-2 border-[#24c35b]/50 hover:border-[#24c35b]/80 transition-all duration-200">
                  {/* Buy button (left side, bigger) */}
                  <button
                    className="flex-1 bg-gradient-to-b from-[#24c35b] to-[#1ea348] hover:from-[#1ea348] hover:to-[#24c35b] text-white font-bold flex items-center justify-center text-[11px] transition-all duration-200 shadow-inner"
                    onClick={handleQuickBuy}
                    title={`Quick buy using global setting: ${quickBuyAmount} SOL`}
                  >
                    BUY
                  </button>
                  {/* Settings button (right side) */}
                  <button
                    className="w-6 bg-white hover:bg-gray-50 text-[#24c35b] hover:text-[#1ea348] flex items-center justify-center transition-all duration-200 border-l border-[#24c35b]/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowQuickBuySettings(true);
                    }}
                    title="Edit global quick buy settings"
                  >
                    <Settings size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Bonding curve bar as part of the card - NO BORDER to eliminate gaps */}
        <div className="h-3 bg-gray-200 relative overflow-hidden">
          {/* Background track - solid color */}
          <div className="absolute inset-0 bg-gray-200" />
          
          {/* Progress fill container with enhanced styling */}
          <div 
            className={`absolute top-0 bottom-0 left-0 transition-all duration-1000 ease-out ${
              category === 'migrated'
                ? 'bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400'
                : 'bg-gradient-to-r from-[#1ea348] via-[#24c35b] to-[#2dd55b]'
            }`}
            style={{ 
              width: `${category === 'migrated' ? 100 : getBondingCurveProgress()}%`,
              minWidth: getBondingCurveProgress() > 0 ? '3px' : '0px',
              boxShadow: category === 'migrated'
                ? '0 0 12px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                : '0 0 12px rgba(36, 195, 91, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
            }}
          >
            {/* Enhanced shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer opacity-60"></div>
            
            {/* Subtle top highlight */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
            
            {/* Progress indicator line at the end */}
            {getBondingCurveProgress() > 0 && getBondingCurveProgress() < 100 && (
              <div className="absolute top-0 bottom-0 right-0 w-[1px] bg-white/60 shadow-lg"></div>
            )}
          </div>
          
          {/* Progress text with improved styling */}
          <div className="absolute inset-0 flex items-center justify-between px-2 z-10">
            <span className={`text-[8px] font-bold drop-shadow-lg ${category === 'migrated' ? 'text-amber-700' : 'text-white'}`}>
              {category === 'migrated' ? 'MIGRATED' : 'BONDING CURVE'}
            </span>
            <span className={`text-[9px] font-bold drop-shadow-lg ${category === 'migrated' ? 'text-amber-800' : 'text-white'}`}>
              {category === 'migrated' ? '100%' : formatPercentage(getBondingCurveProgress())}
            </span>
          </div>
        </div>
      </div>
      
      {/* Quick Buy Settings Popup */}
      <QuickBuySettings
        isOpen={showQuickBuySettings}
        onClose={() => setShowQuickBuySettings(false)}
        onSelectAmount={(amount) => {
          updateQuickBuySettings(amount);
        }}
        defaultAmount={quickBuyAmount}
        token={token}
      />
    </div>
  );
};

// Create a utility function outside the component for calculating relative time
// 🕐 **FIXED**: Now uses received_at timestamp (when our system received the token) 
// instead of blockchain creation time for accurate "time ago" display
const calculateTimeSince = (dateString: string | number | null): string => {
  if (!dateString) return '';
  
  // Convert to string if it's a number (timestamp)
  const dateStr = typeof dateString === 'number' ? new Date(dateString).toISOString() : dateString;
  
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) {
    return `${seconds}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

// Update the FluidAnimations component with more realistic fluid animations
const FluidAnimations = () => (
  <style>
    {`
    @keyframes fluid {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
    
    @keyframes shimmer {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }
    
    @keyframes wave {
      0% {
        transform: translateX(0) translateY(0);
      }
      50% {
        transform: translateX(-25%) translateY(-30%);
      }
      100% {
        transform: translateX(0) translateY(0);
      }
    }
    
    @keyframes bubble-up {
      0% {
        transform: translateY(100%) scale(0);
        opacity: 0;
      }
      50% {
        opacity: 0.8;
      }
      100% {
        transform: translateY(-100%) scale(1);
        opacity: 0;
      }
    }
    

    
    .animate-fluid {
      background-size: 200% 100%;
      animation: fluid 3s ease infinite;
    }
    
    .animate-shimmer {
      animation: shimmer 2s infinite;
    }
    
    .wave-top {
      position: absolute;
      top: -120%;
      left: -25%;
      width: 150%;
      height: 150%;
      background: rgba(36, 195, 91, 0.7);
      border-radius: 40%;
      animation: wave 5s linear infinite;
    }
    
    .wave-middle {
      position: absolute;
      top: -120%;
      left: -35%;
      width: 170%;
      height: 150%;
      background: rgba(36, 195, 91, 0.5);
      border-radius: 35%;
      animation: wave 7s linear infinite;
      animation-delay: 0.5s;
    }
    
    .bubbles {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    
    .bubble {
      position: absolute;
      bottom: 0;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      animation: bubble-up 4s ease-in infinite;
    }
    
    .bubble-1 {
      width: 4px;
      height: 4px;
      left: 10%;
      animation-duration: 3s;
    }
    
    .bubble-2 {
      width: 3px;
      height: 3px;
      left: 50%;
      animation-delay: 1s;
      animation-duration: 4s;
    }
    
    .bubble-3 {
      width: 5px;
      height: 5px;
      left: 80%;
      animation-delay: 2s;
      animation-duration: 5s;
    }
    `}
  </style>
);

// Create a memoized version of the TokenCard that only rerenders when props actually change
const TokenCard = memo(TokenCardComponent, (prevProps, nextProps) => {
  if (prevProps.highlight !== nextProps.highlight) return false;
  if (prevProps.token === nextProps.token) return true;
  if (prevProps.token.mint !== nextProps.token.mint) return false;

  const prevImageUrl = prevProps.token.image_url || prevProps.token.image_uri;
  const nextImageUrl = nextProps.token.image_url || nextProps.token.image_uri;
  if (prevImageUrl !== nextImageUrl) return false;

  if (prevProps.token.userBalance !== nextProps.token.userBalance) return false;
  if (prevProps.token.price_usd !== nextProps.token.price_usd) return false;
  if (prevProps.token.market_cap_usd !== nextProps.token.market_cap_usd) return false;
  if (prevProps.token.bonding_curve_progress !== nextProps.token.bonding_curve_progress) return false;

  // Add these lines:
  if (prevProps.token.buys_count !== nextProps.token.buys_count) return false;
  if (prevProps.token.sells_count !== nextProps.token.sells_count) return false;
  if (prevProps.token.volume_usd !== nextProps.token.volume_usd) return false;
  
  // Check developer sell status
  if (prevProps.token.dev_sell_time !== nextProps.token.dev_sell_time) return false;

  return true;
});

// Add the animations to the component export
export default function TokenCardWithAnimations(props: TokenCardProps) {
  return (
    <>
      <FluidAnimations />
      <TokenCard {...props} />
    </>
  );
}

// Add a utility function to safely validate URLs before rendering
const isValidUrl = (url: string | null): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

// Add a utility function to check if token is in any active feed